import { useCallback, useState } from 'react';
import type { JSX, ReactElement, SyntheticEvent } from 'react';

import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import VisibilityIcon from '@mui/icons-material/Visibility';
import IconButton from '@mui/material/IconButton';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Switch from '@mui/material/Switch';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CreateIcon from '@mui/icons-material/Create';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DownloadIcon from '@mui/icons-material/Download';
import StartIcon from '@mui/icons-material/Start';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InsertPageBreakIcon from '@mui/icons-material/InsertPageBreak';
import VrpanoIcon from '@mui/icons-material/Vrpano';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import {
  ContentOriginEnum,
  OrientationEnum,
  PaperFormatEnum,
} from '@e-socna/data-layer';
import { BooleanFilter } from '@e-socna/utils';
import { EMPTY_OBJECT, NULL_FUNCTION } from '@e-socna/empty';
import type { ContentOriginId } from '@e-socna/data-layer';

import { useReferencesContext } from '../../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { stopPropagation } from '../../../../utils/stop-propagation.util';

import { DocumentEditorTwoStepContentActionType } from '../../enums/two-step-content-action-type.enum';
import { useDocumentEditorContext } from '../../contexts/DocumentEditorContext/hooks/useDocumentEditorContext.hook';
import type { EnrichedSection } from '../../types/enriched-section.type';
import type { Editable } from '../../types/editable.type';
import type { DocumentEditorCmp_SectionDisplayCondition } from '../../fragments/recursive-section.fragment';

import { DisplayConditionDialog } from './DisplayConditionDialog';

interface MenuActionEntry {
  readonly kind: 'action';
  readonly key: string;
  readonly icon: ReactElement;
  readonly label: string;
  readonly onSelect: () => void;
}

interface MenuToggleEntry {
  readonly kind: 'toggle';
  readonly key: string;
  readonly icon: ReactElement;
  readonly label: string;
  readonly ariaLabelledBy: string;
  readonly checked: boolean;
  readonly onToggle: () => void;
}

interface MenuDividerEntry {
  readonly kind: 'divider';
  readonly key: string;
}

type MenuEntry = MenuActionEntry | MenuToggleEntry | MenuDividerEntry;

interface BuildMenuEntriesParams {
  readonly section: Readonly<EnrichedSection>;
  readonly isReinitializable: boolean;
  readonly onModify: () => void;
  readonly onInsertBefore: () => void;
  readonly onInsertAfter: () => void;
  readonly onInsertInside: () => void;
  readonly onInsertStart: () => void;
  readonly onInsertEnd: () => void;
  readonly onTogglePageBreakBefore: () => void;
  readonly onTogglePageBreakAfter: () => void;
  readonly onToggleOrientation: () => void;
  readonly onTogglePaperFormat: () => void;
  readonly onEditDisplayCondition: () => void;
  readonly onDuplicate: () => void;
  readonly onReinitialize: () => void;
  readonly onDelete: () => void;
}

const buildMenuEntries = ({
  section,
  isReinitializable,
  onModify,
  onInsertBefore,
  onInsertAfter,
  onInsertInside,
  onInsertStart,
  onInsertEnd,
  onTogglePageBreakBefore,
  onTogglePageBreakAfter,
  onToggleOrientation,
  onTogglePaperFormat,
  onEditDisplayCondition,
  onDuplicate,
  onReinitialize,
  onDelete,
}: Readonly<BuildMenuEntriesParams>): readonly MenuEntry[] => {
  const hasSubSections = !!section.subSections.length;

  const entries: readonly (MenuEntry | false)[] = [
    !section.isVirtual && {
      kind: 'action',
      key: 'modify',
      icon: <CreateIcon fontSize="small" />,
      label: 'Modifier',
      onSelect: onModify,
    },
    !section.isVirtual && { kind: 'divider', key: 'divider1' },
    !hasSubSections && {
      kind: 'action',
      key: 'insertInside',
      icon: <StartIcon fontSize="small" />,
      label: 'Insérer Contenu',
      onSelect: onInsertInside,
    },
    !section.oneChildMax &&
      hasSubSections && {
        kind: 'action',
        key: 'insertStart',
        icon: (
          <DownloadIcon fontSize="small" sx={{ transform: 'rotate(180deg)' }} />
        ),
        label: 'Insérer au Début',
        onSelect: onInsertStart,
      },
    !section.oneChildMax &&
      hasSubSections && {
        kind: 'action',
        key: 'insertEnd',
        icon: <DownloadIcon fontSize="small" />,
        label: 'Insérer à la Fin',
        onSelect: onInsertEnd,
      },
    (!hasSubSections || !section.oneChildMax) && {
      kind: 'divider',
      key: 'divider2',
    },
    !section.noSiblings && {
      kind: 'action',
      key: 'insertBefore',
      icon: <FileUploadIcon fontSize="small" />,
      label: 'Insérer Avant',
      onSelect: onInsertBefore,
    },
    !section.hasLockedPagebreaks && {
      kind: 'toggle',
      key: 'pagebreakBefore',
      icon: <InsertPageBreakIcon fontSize="small" />,
      label: 'Saut de Page Avant',
      ariaLabelledBy: `menu-item-pagebreak-before-${section._id}`,
      checked: section.hasPagebreakBefore,
      onToggle: onTogglePageBreakBefore,
    },
    (!section.noSiblings || !section.hasLockedPagebreaks) && {
      kind: 'divider',
      key: 'divider3',
    },
    !section.noSiblings && {
      kind: 'action',
      key: 'insertAfter',
      icon: (
        <FileUploadIcon fontSize="small" sx={{ transform: 'rotate(180deg)' }} />
      ),
      label: 'Insérer Après',
      onSelect: onInsertAfter,
    },
    !section.hasLockedPagebreaks && {
      kind: 'toggle',
      key: 'pagebreakAfter',
      icon: <InsertPageBreakIcon fontSize="small" />,
      label: 'Saut de Page Après',
      ariaLabelledBy: `menu-item-pagebreak-after-${section._id}`,
      checked: section.hasPagebreakAfter,
      onToggle: onTogglePageBreakAfter,
    },
    (!section.noSiblings || !section.hasLockedPagebreaks) && {
      kind: 'divider',
      key: 'divider4',
    },
    !section.noSiblings && {
      kind: 'action',
      key: 'duplicate',
      icon: <ContentCopyIcon fontSize="small" />,
      label: 'Dupliquer',
      onSelect: onDuplicate,
    },
    !section.noSiblings && { kind: 'divider', key: 'divider5' },
    {
      kind: 'toggle',
      key: 'orientation',
      icon: <VrpanoIcon fontSize="small" />,
      label: 'Orientation Paysage',
      ariaLabelledBy: `menu-item-landscape-orientation-${section._id}`,
      checked: section.orientation === OrientationEnum.Landscape,
      onToggle: onToggleOrientation,
    },
    {
      kind: 'toggle',
      key: 'paperFormat',
      icon: <AspectRatioIcon fontSize="small" />,
      label: 'Format A3',
      ariaLabelledBy: `menu-item-a3-paper-format-${section._id}`,
      checked: section.paperFormat === PaperFormatEnum.A3,
      onToggle: onTogglePaperFormat,
    },
    // The display condition of an inherited section is the one of its
    // template.
    !section.isVirtual &&
      !section.insertionPoint && {
        // The switch tells whether the section has a condition; any click
        // opens the condition dialog.
        kind: 'toggle',
        key: 'displayCondition',
        icon: <VisibilityIcon fontSize="small" />,
        label: "Condition d'Affichage",
        ariaLabelledBy: `menu-item-display-condition-${section._id}`,
        checked: !!section.displayCondition,
        onToggle: onEditDisplayCondition,
      },
    { kind: 'divider', key: 'divider6' },
    isReinitializable && {
      kind: 'action',
      key: 'reinitialize',
      icon: <RestartAltIcon fontSize="small" />,
      label: 'Réinitialiser',
      onSelect: onReinitialize,
    },
    {
      kind: 'action',
      key: 'delete',
      icon: <DeleteForeverIcon fontSize="small" />,
      label: 'Supprimer',
      onSelect: onDelete,
    },
  ];

  return entries.filter(BooleanFilter);
};

const renderMenuEntry = (entry: Readonly<MenuEntry>): JSX.Element => {
  switch (entry.kind) {
    case 'divider': {
      return <Divider key={entry.key} />;
    }

    case 'action': {
      return (
        // eslint-disable-next-line react/jsx-handler-names
        <MenuItem key={entry.key} onClick={entry.onSelect}>
          <ListItemIcon>{entry.icon}</ListItemIcon>
          <ListItemText>{entry.label}</ListItemText>
        </MenuItem>
      );
    }

    case 'toggle': {
      return (
        // eslint-disable-next-line react/jsx-handler-names
        <MenuItem key={entry.key} onClick={entry.onToggle}>
          <ListItemIcon>{entry.icon}</ListItemIcon>
          <ListItemText id={entry.ariaLabelledBy}>{entry.label}</ListItemText>
          <Switch
            checked={entry.checked}
            edge="end"
            onChange={NULL_FUNCTION}
            size="small"
            slotProps={{
              input: { 'aria-labelledby': entry.ariaLabelledBy },
            }}
            sx={{ ml: 4 }}
          />
        </MenuItem>
      );
    }

    default: {
      const exhaustiveCheck: never = entry;
      throw new Error(`Unknown menu entry: ${JSON.stringify(exhaustiveCheck)}`);
    }
  }
};

interface Props {
  readonly section: EnrichedSection;
}

export const SectionMenu = ({ section }: Readonly<Props>) => {
  const { contentOriginsIdMap } = useReferencesContext();

  const { actions } = useDocumentEditorContext<Editable>();

  const [anchorEl, setAnchorEl] = useState<null | Element>(null);
  const open = Boolean(anchorEl);

  const handleClick = useCallback((event: Readonly<SyntheticEvent>) => {
    stopPropagation(event);
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback((event: Readonly<SyntheticEvent>) => {
    stopPropagation(event);
    setAnchorEl(null);
  }, []);

  const modify = useCallback(() => {
    actions.requestTwoStepContentAction({
      twoStepContentActionType: DocumentEditorTwoStepContentActionType.Update,
      autoBackup: true,
      path: section.realPath,
      alreadyUsedIds: section.alreadyUsedIds,
      insertionPointRealPath: section.insertionPoint?.realPath,
      textualContent: {
        _id: section._id,
        title: section.title,
        content: section.content,
      },
      orientation: section.orientation,
      paperFormat: section.paperFormat,
    });
    setAnchorEl(null);
  }, [
    section.realPath,
    actions,
    section.alreadyUsedIds,
    section.insertionPoint?.realPath,
    section.content,
    section.title,
    section._id,
    section.orientation,
    section.paperFormat,
  ]);

  const insertBefore = useCallback(() => {
    actions.requestTwoStepContentAction({
      twoStepContentActionType:
        DocumentEditorTwoStepContentActionType.InsertBefore,
      autoBackup: true,
      path: section.realPath,
      alreadyUsedIds: section.alreadyUsedIds.slice(0, -1),
      insertionPointRealPath: section.insertionPoint?.realPath,
    });
    setAnchorEl(null);
  }, [
    section.realPath,
    actions,
    section.alreadyUsedIds,
    section.insertionPoint?.realPath,
  ]);

  const insertAfter = useCallback(() => {
    actions.requestTwoStepContentAction({
      twoStepContentActionType:
        DocumentEditorTwoStepContentActionType.InsertAfter,
      autoBackup: true,
      path: section.realPath,
      alreadyUsedIds: section.alreadyUsedIds.slice(0, -1),
      insertionPointRealPath: section.insertionPoint?.realPath,
    });
    setAnchorEl(null);
  }, [
    section.realPath,
    actions,
    section.alreadyUsedIds,
    section.insertionPoint?.realPath,
  ]);

  const insertInside = useCallback(() => {
    actions.requestTwoStepContentAction({
      twoStepContentActionType:
        DocumentEditorTwoStepContentActionType.InsertInside,
      autoBackup: true,
      path: section.realPath,
      alreadyUsedIds: section.alreadyUsedIds,
      insertionPointRealPath: section.insertionPoint?.realPath,
      orientation: section.orientation,
      paperFormat: section.paperFormat,
      ...(section.oneChildMax ? { onlyOneChoice: true } : EMPTY_OBJECT),
    });
    setAnchorEl(null);
  }, [
    section.realPath,
    actions,
    section.alreadyUsedIds,
    section.insertionPoint?.realPath,
    section.orientation,
    section.paperFormat,
    section.oneChildMax,
  ]);

  const insertStart = useCallback(() => {
    actions.requestTwoStepContentAction({
      twoStepContentActionType:
        DocumentEditorTwoStepContentActionType.InsertStart,
      autoBackup: true,
      path: section.realPath,
      alreadyUsedIds: section.alreadyUsedIds,
      insertionPointRealPath: section.isInsertionPoint
        ? section.realPath
        : section.insertionPoint?.realPath,
      orientation: section.orientation,
      paperFormat: section.paperFormat,
    });
    setAnchorEl(null);
  }, [
    section.isInsertionPoint,
    section.realPath,
    actions,
    section.alreadyUsedIds,
    section.insertionPoint?.realPath,
    section.orientation,
    section.paperFormat,
  ]);

  const insertEnd = useCallback(() => {
    actions.requestTwoStepContentAction({
      twoStepContentActionType:
        DocumentEditorTwoStepContentActionType.InsertEnd,
      autoBackup: true,
      path: section.realPath,
      alreadyUsedIds: section.alreadyUsedIds,
      insertionPointRealPath: section.isInsertionPoint
        ? section.realPath
        : section.insertionPoint?.realPath,
      orientation: section.orientation,
      paperFormat: section.paperFormat,
    });
    setAnchorEl(null);
  }, [
    section.isInsertionPoint,
    section.realPath,
    actions,
    section.alreadyUsedIds,
    section.insertionPoint?.realPath,
    section.orientation,
    section.paperFormat,
  ]);

  const togglePageBreakBefore = useCallback(() => {
    const hasPagebreakBefore = !section.hasPagebreakBefore;

    if (section.insertionPoint) {
      actions.setCustomPageBreakBefore(
        section.insertionPoint.realPath,
        section._id,
        hasPagebreakBefore,
      );

      return;
    }

    actions.setPageBreakBefore(section.realPath, hasPagebreakBefore);
  }, [
    section._id,
    section.realPath,
    section.insertionPoint,
    section.hasPagebreakBefore,
    actions,
  ]);

  const togglePageBreakAfter = useCallback(() => {
    const hasPagebreakAfter = !section.hasPagebreakAfter;

    if (section.insertionPoint) {
      actions.setCustomPageBreakAfter(
        section.insertionPoint.realPath,
        section._id,
        hasPagebreakAfter,
      );

      return;
    }

    actions.setPageBreakAfter(section.realPath, hasPagebreakAfter);
  }, [
    section._id,
    section.realPath,
    section.insertionPoint,
    section.hasPagebreakAfter,
    actions,
  ]);

  const toggleOrientation = useCallback(() => {
    actions.setOrientation(
      section,
      section.orientation === OrientationEnum.Landscape
        ? OrientationEnum.Portrait
        : OrientationEnum.Landscape,
    );
  }, [section, actions]);

  const togglePaperFormat = useCallback(() => {
    actions.setPaperFormat(
      section,
      section.paperFormat === PaperFormatEnum.A3
        ? PaperFormatEnum.A4
        : PaperFormatEnum.A3,
    );
  }, [section, actions]);

  const [isDisplayConditionDialogOpen, setIsDisplayConditionDialogOpen] =
    useState(false);

  const openDisplayConditionDialog = useCallback(() => {
    setAnchorEl(null);
    setIsDisplayConditionDialogOpen(true);
  }, []);

  const closeDisplayConditionDialog = useCallback(() => {
    setIsDisplayConditionDialogOpen(false);
  }, []);

  const setDisplayCondition = useCallback(
    (displayCondition: DocumentEditorCmp_SectionDisplayCondition | null) => {
      actions.setDisplayCondition(section.realPath, displayCondition);
    },
    [section.realPath, actions],
  );

  const deleteSection = useCallback(() => {
    actions.deleteSection(section.realPath, section.insertionPoint?.realPath);
    setAnchorEl(null);
  }, [section.realPath, actions, section.insertionPoint?.realPath]);

  const reinitializeSection = useCallback(() => {
    actions.reinitializeSection(
      section.realPath,
      section.insertionPoint?.realPath,
    );
    setAnchorEl(null);
  }, [section.realPath, actions, section.insertionPoint?.realPath]);

  const duplicateSection = useCallback(() => {
    actions.duplicateSection(
      section.realPath,
      section.insertionPoint?.realPath,
    );
    setAnchorEl(null);
  }, [section.realPath, actions, section.insertionPoint?.realPath]);

  const isReinitializable = !!(
    section.isInsertionPoint &&
    contentOriginsIdMap[section.contentOriginId as ContentOriginId]?.code ===
      ContentOriginEnum.Modified
  );

  const entries = buildMenuEntries({
    section,
    isReinitializable,
    onModify: modify,
    onInsertBefore: insertBefore,
    onInsertAfter: insertAfter,
    onInsertInside: insertInside,
    onInsertStart: insertStart,
    onInsertEnd: insertEnd,
    onTogglePageBreakBefore: togglePageBreakBefore,
    onTogglePageBreakAfter: togglePageBreakAfter,
    onToggleOrientation: toggleOrientation,
    onTogglePaperFormat: togglePaperFormat,
    onEditDisplayCondition: openDisplayConditionDialog,
    onDuplicate: duplicateSection,
    onReinitialize: reinitializeSection,
    onDelete: deleteSection,
  });

  return (
    <>
      <IconButton
        aria-controls={open ? `${section._id}-long-menu` : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        aria-label="Plus d'actions"
        component="span"
        id={`${section._id}-menu-button`}
        onClick={handleClick}
        role="button"
        tabIndex={0}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        id={`${section._id}-long-menu`}
        onClick={stopPropagation}
        onClose={handleClose}
        open={open}
        slotProps={{
          list: { 'aria-labelledby': `${section._id}-menu-button` },
        }}
      >
        {entries.map(renderMenuEntry)}
      </Menu>
      {isDisplayConditionDialogOpen ? (
        <DisplayConditionDialog
          displayCondition={section.displayCondition}
          isOpen
          onClose={closeDisplayConditionDialog}
          onValidate={setDisplayCondition}
        />
      ) : null}
    </>
  );
};
